import { useState, useEffect, useRef, useCallback, lazy } from 'react';
import { LazyShell } from './core/errors/LazyShell';
import { RoleGate } from './core/access/RoleGate';
import { ROUTES } from './core/routing/routes';
import Layout, { type LayoutChildProps } from './core/layout/Layout';
import ChatRoom from './modules/chat/ChatRoom';
import { createGroup, type ConversationSummary } from './modules/networking/service';
import { trackTabChange } from './analytics';
import { useSessionTimeout } from './core/auth/useSessionTimeout';
import { useCheckoutSuccess } from './core/auth/useCheckoutSuccess';
import { useVisualViewport } from './hooks/useVisualViewport';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { SESSION_WARNING_MS, SESSION_TIMEOUT_MS, LOGIN_PATH } from './constants';

// Heavy tabs are lazy-loaded so they don't bloat the initial bundle
const NetworkingView = lazy(() => import('./modules/networking/NetworkingView'));
const EducationView  = lazy(() => import('./modules/education/EducationView'));
const ProfileView    = lazy(() => import('./modules/user/ProfileView'));
const SettingsView   = lazy(() => import('./modules/user/SettingsView'));
const HomeView       = lazy(() => import('./modules/home/HomeView'));
const ConversationsView = lazy(() => import('./modules/messaging/ConversationsView'));
import { getMessagesForChat } from './modules/chat/chatservice';
import { useStreamingChat } from './modules/chat/useStreamingChat';
import { createChat } from './modules/chat/chatMetadataService';
import { useCurrentUser } from './core/auth/UserProvider';
import { moveChatToProject } from './modules/chat/chatservice';
import { useToastContext } from './core/ui/toast/ToastProvider';
import { emit as emitAppEvent, on as onAppEvent } from './core/events/bus';
import CheckoutSuccessModal from './modules/user/CheckoutSuccessModal';
import './App.css';

export type TabType = 'home' | 'assistant' | 'education' | 'networking' | 'mentorship' | 'messages' | 'settings' | 'profile';
export type EducationSubTab = 'bootcamp' | 'coaching';
// Groups were folded back into the Messages tab; Networking is now
// purely people-discovery. `readLS` may still return the legacy
// 'groups' value from old sessions — coerce that to 'connections'.
export type NetworkingSubTab = 'connections' | 'requests' | 'discover';

export type ConnectionStatus = 'connected' | 'pending_sent' | 'pending_received' | 'none';

export type Professional = {
    id: string;
    name: string;
    email: string;
    role: string;
    company: string;
    location: string;
    avatar: string;
    online: boolean;
    skills: string[];
    connectionStatus: ConnectionStatus;
};

// Local view-model for the currently-active group chat tile — distinct
// from the wire-shaped `GroupChat` DTO in `modules/networking/service.ts`.
// Phase D will fold both into `modules/messaging/types.ts` with explicit
// DTO / ViewModel suffixes. Until then, keep this local and narrowly used
// (consumed only by `App.tsx`'s `activeGroupChat` state + seed-to-ConversationSummary mapping).
type ActiveGroupChat = {
    id: string;
    name: string;
    members: Professional[];
    lastMessage?: string;
    lastMessageTime?: string;
    unreadCount?: number;
};

export type MessageFile = {
    id: string;
    file: string;
    thumbnail?: string;
    original_name: string;
    file_type: string;
};

export type MessageStatus = 'ok' | 'streaming' | 'cancelled' | 'error';

export type MessageUsage = {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
};

export type Message = {
    id: string;
    order: number;
    role: 'user' | 'assistant' | 'tool';
    content: string;
    tool_calls?: ToolCall[];
    tool_call_id?: string;
    streaming?: boolean;
    files?: MessageFile[];
    /** Lifecycle hint from the backend — used to render the stopped /
     *  errored state on bubbles that never completed cleanly. */
    status?: MessageStatus;
    /** Token counts populated on `message_complete`; admin-only chip reads these. */
    usage?: MessageUsage | null;
    /** Provider model identifier (gpt-5.1, etc.) — admin chip only. */
    model?: string | null;
    /** Turn cost in USD as a string (backend ships Decimal serialized). */
    cost_usd?: string | null;
    /** Provider finish_reason — 'stop' | 'length' | 'tool_calls' | ... */
    finish_reason?: string | null;
    /** Wall-clock latency reported by the server. Admin chip only. */
    latency_ms?: number | null;
};

export type ToolCall = {
    id: string;
    type: string;
    function: {
        name: string;
        arguments: string;
    };
};

// ── localStorage helpers for navigation persistence ─────────────────
function readLS<T>(key: string, fallback: T): T {
    try {
        const raw = localStorage.getItem(key);
        return raw !== null ? (JSON.parse(raw) as T) : fallback;
    } catch { return fallback; }
}
function writeLS(key: string, value: unknown) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
}

const TAB_TITLES: Record<TabType, string> = {
    home: 'Home',
    assistant: 'Assistant',
    education: 'Education',
    networking: 'Networking',
    mentorship: 'Mentorship',
    messages: 'Messages',
    settings: 'Settings',
    profile: 'Profile',
};

function App() {
    useVisualViewport();
    const { addToast } = useToastContext();
    const [activeTab, setActiveTab] = useState<TabType>(() => readLS('nav_activeTab', 'assistant'));
    useDocumentTitle(TAB_TITLES[activeTab] ?? null);
    const [educationSubTab, setEducationSubTab] = useState<EducationSubTab>(() => readLS('nav_educationSubTab', 'bootcamp'));
    const [networkingSubTab, setNetworkingSubTab] = useState<NetworkingSubTab>(() => {
        const raw = readLS<string>('nav_networkingSubTab', 'connections');
        return raw === 'connections' || raw === 'requests' || raw === 'discover'
            ? raw
            : 'connections';
    });
    const [selectedChatId, setSelectedChatId] = useState<string | null>(() => readLS('nav_selectedChatId', null));
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => readLS('nav_selectedProjectId', null));
    const [dmUser, setDmUser] = useState<Professional | null>(null);
    const [activeGroupChat, setActiveGroupChat] = useState<ActiveGroupChat | null>(null);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const userData = useCurrentUser();

    // Streaming chat hook — SSOT for assistant turn state.
    const {
        messages,
        setMessages,
        send: sendStream,
        stop: stopStream,
        regenerate: regenerateStream,
        state: chatState,
        lastError: chatLastError,
    } = useStreamingChat({
        chatId: selectedChatId,
        onUsageRefresh: () => emitAppEvent('user-invalidate', { reason: 'profile-save' }),
        // quota_warning intentionally does NOT fire a toast: the
        // persistent ChatQuotaBanner is the authoritative "you're near
        // the cap" UI. Firing a toast in parallel creates a double-CTA
        // collision (banner + toast both surface Upgrade) that looks
        // broken. If you need a stronger nudge, animate the banner
        // instead of layering a toast on top.
        onQuotaExhausted: () => {
            addToast(
                "You've used all your monthly tokens. Upgrade to keep chatting.",
                'error',
            );
            // Route to Billing so the upgrade path is one click away.
            emitAppEvent('open-plans', { reason: 'quota' });
        },
    });
    // Surface transient stream errors via the toast system.
    useEffect(() => {
        if (chatLastError) {
            addToast(chatLastError.message || 'Chat error. Please try again.', 'error');
        }
    }, [chatLastError, addToast]);
    const [sidebarProjects, setSidebarProjects] = useState<{ id: string; name: string }[]>([]);
    const skipLoadForChatIdRef = useRef<string | null>(null);
    const [showSessionWarning, setShowSessionWarning] = useState(false);

    // Session timeout: warn user 5 min before JWT likely expires
    const handleSessionWarning = useCallback(() => {
        setShowSessionWarning(true);
    }, []);
    // Persist navigation state to localStorage
    useEffect(() => { writeLS('nav_activeTab', activeTab); }, [activeTab]);
    useEffect(() => { writeLS('nav_educationSubTab', educationSubTab); }, [educationSubTab]);
    useEffect(() => { writeLS('nav_networkingSubTab', networkingSubTab); }, [networkingSubTab]);
    useEffect(() => { writeLS('nav_selectedChatId', selectedChatId); }, [selectedChatId]);
    useEffect(() => { writeLS('nav_selectedProjectId', selectedProjectId); }, [selectedProjectId]);

    const handleSessionTimeout = useCallback(() => {
        setShowSessionWarning(false);
        window.location.href = LOGIN_PATH;
    }, []);
    useSessionTimeout({
        warningAfterMs: SESSION_WARNING_MS,
        logoutAfterMs: SESSION_TIMEOUT_MS,
        onWarning: handleSessionWarning,
        onTimeout: handleSessionTimeout,
        enabled: !!userData,  // only when logged in
    });

    // Listen for sidebar broadcasting its project list
    useEffect(() => {
        return onAppEvent('sidebar-projects-loaded', (payload) => {
            setSidebarProjects(payload?.projects ?? []);
        });
    }, []);

    const handleTabChange = (tab: TabType, subTab?: string) => {
        setActiveTab(tab);
        trackTabChange(subTab ? `${tab}:${subTab}` : tab);
        if (tab === 'education' && subTab) {
            setEducationSubTab(subTab as EducationSubTab);
        } else if (tab === 'networking' && subTab) {
            setNetworkingSubTab(subTab as NetworkingSubTab);
        }
    };

    // UserProvider owns the initial fetch + focus refetch; see
    // core/auth/UserProvider.tsx. App.tsx only triggers refetches via
    // the `user-invalidate` bus event (e.g. after a message that
    // consumed tokens) so there's one source of truth for user state.

    // After Stripe checkout, the success handler redirects back here
    // with ?checkout=success. Hook detects, invalidates user, and
    // exposes modal open/close. See useCheckoutSuccess.
    const { open: showCheckoutSuccess, close: closeCheckoutSuccess } = useCheckoutSuccess();

    // Phase 3 redirect shim: PaywallGate + Sidebar + older surfaces
    // dispatch 'open-plans' to surface the billing page. Post-Phase-3
    // Profile no longer owns billing, so route to Settings › Billing.
    // Phase 4 will add an in-pane jump via hash so the Billing section
    // auto-opens on arrival.
    useEffect(() => {
        return onAppEvent('open-plans', () => {
            setActiveTab('settings');
            if (typeof window !== 'undefined') {
                window.location.hash = '#billing';
            }
        });
    }, []);

    // Cross-module: Networking Accept + Mentor Review-list emit
    // `open-dm-with-user` so the user lands on a seeded DM thread
    // without having to tab-hunt. Minimal Professional shape is
    // enough — ConversationsView fetches the rest.
    useEffect(() => {
        return onAppEvent('open-dm-with-user', (payload) => {
            if (!payload?.userId) return;
            const stub: Professional = {
                id: payload.userId,
                name: payload.name ?? 'Member',
                email: '',
                role: '',
                company: '',
                location: '',
                avatar: payload.avatar ?? '',
                online: false,
                skills: [],
                connectionStatus: 'connected',
            };
            setDmUser(stub);
            setActiveGroupChat(null);
            setActiveTab('messages');
        });
    }, []);

    // Cross-module: Mentor review list's inline "Review" button
    // jumps to the Mentorship tab where the assessment flow lives.
    // For now the routing is tab-level; deep link by submissionId
    // arrives once the mentorship view accepts an initialSubmission prop.
    useEffect(() => {
        return onAppEvent('open-submission-review', () => {
            setActiveTab('mentorship');
        });
    }, []);

    // Load messages when a chat is selected
    useEffect(() => {
        if (!selectedChatId) {
            setMessages([]);
            setLoadingMessages(false);
            return;
        }

        // When we create a new projectless chat and immediately start streaming,
        // we don't want to fetch messages in parallel (it can race and overwrite UI).
        if (skipLoadForChatIdRef.current === selectedChatId) {
            skipLoadForChatIdRef.current = null;
            return;
        }

        let cancelled = false;
        async function loadMessages() {
            setLoadingMessages(true);
            try {
                const chatMessages = await getMessagesForChat(selectedChatId!);
                if (!cancelled) setMessages(chatMessages);
            } catch (error: any) {
                if (!cancelled) {
                    if (error?.response?.status === 404) {
                        setSelectedChatId(null);
                    } else {
                        console.error('Failed to load messages:', error);
                    }
                }
            } finally {
                if (!cancelled) setLoadingMessages(false);
            }
        }
        loadMessages();
        return () => { cancelled = true; };
    }, [selectedChatId, setMessages]);

    const handleSendMessage = async (content: string, file?: File | null) => {
        try {
            let chatId = selectedChatId;

            if (!chatId && selectedProjectId) {
                // Project chat: create on first message with auto-naming
                // Note: We currently create chat with text only. 
                // If there's a file, it will be sent in the subsequent stream call.
                const created = await createChat(selectedProjectId, null, content);
                chatId = created.id;

                skipLoadForChatIdRef.current = chatId;
                setSelectedChatId(chatId);

                // Let the sidebar add the new chat under the project.
                emitAppEvent('project-chat-created', {
                    projectId: selectedProjectId,
                    chat: { id: created.id, name: created.name },
                });
            } else if (!chatId) {
                // Projectless direct chat: create on first message with auto-naming
                const created = await createChat(null, null, content);
                chatId = created.id;

                setSelectedProjectId(null);
                                skipLoadForChatIdRef.current = chatId;
                setSelectedChatId(chatId);

                emitAppEvent('direct-chat-created', {
                    id: created.id,
                    name: created.name,
                });
            }

            // Bump chat to top based on context
            if (chatId && selectedProjectId) {
                emitAppEvent('project-chat-touched', { projectId: selectedProjectId, chatId });
            } else if (chatId && selectedProjectId === null) {
                emitAppEvent('direct-chat-touched', { chatId });
            }

            await sendStream(content, file ?? null, { chatId });

            // `useStreamingChat` fires `user-invalidate` via `onUsageRefresh`
            // on message completion, so no extra emit needed here.
        } catch (error) {
            console.error('Failed to send message:', error);
            addToast('Failed to send message. Please try again.', 'error');
        }
    };

    const handleNewDirectChat = () => {
                setSelectedChatId(null);
        setSelectedProjectId(null);
        setMessages([]);
        setLoadingMessages(false);
    };

    const handleMoveToProject = async (targetProjectId: string | null) => {
        if (!selectedChatId) {
            // New chat — just update the selected project so next message creates under it
            setSelectedProjectId(targetProjectId);
            return;
        }
        // Existing chat — call API to move it
        try {
            await moveChatToProject(selectedChatId, targetProjectId);
            setSelectedProjectId(targetProjectId);
            // Notify sidebar to refresh
            emitAppEvent('chat-moved', {
                chatId: selectedChatId,
                fromProjectId: selectedProjectId,
                toProjectId: targetProjectId,
            });
        } catch (error) {
            console.error('Failed to move chat:', error);
            addToast('Failed to move chat to project', 'error');
        }
    };

    const handleCreateProjectFromChat = () => {
        // Sidebar listens and opens the create-project modal. After the
        // project is created, the sidebar attaches the currently-selected
        // chat (if any) to the new project via its own mutation path.
        emitAppEvent('open-create-project-modal', { chatId: selectedChatId });
    };

    const handleChatSelect = (_projectId: string, chatId: string) => {
        const normalizedProjectId = _projectId ? _projectId : null;
        setSelectedProjectId(normalizedProjectId);
        setActiveTab('assistant');

        // Always close the right panel when choosing a chat.
        
        // Empty chatId means "new chat in project" — clear state and wait for first message.
        if (!chatId) {
            setSelectedChatId(null);
            setMessages([]);
            setLoadingMessages(false);
            return;
        }

        // If the user clicks the chat they're already in, don't reload.
        if (chatId === selectedChatId) return;

        // Clear old messages immediately so we don't show the previous chat while loading.
        setMessages([]);
        setLoadingMessages(true);
        setSelectedChatId(chatId);
    };

    const handleMessageUser = (professional: Professional) => {
        // Messaging is its own top-level tab now — jump to it and open
        // the thread. Previously this kept activeTab='networking' which
        // was misleading (sidebar highlighted Networking even though
        // you were reading DMs).
        setDmUser(professional);
        setActiveGroupChat(null);
        setActiveTab('messages');
    };

    // Retained for ConversationsView's internal back handler: user taps
    // back from a seeded detail pane → clear the seed so the list is
    // shown instead.
    const _handleBackFromDm = () => {
        setDmUser(null);
        setActiveGroupChat(null);
    };
    void _handleBackFromDm;

    const handleCreateGroup = async (name: string, members: Professional[]) => {
        // Real backend create — uses the service helper which hits the
        // correct POST /api/networking/groups/create/ endpoint. The
        // previous inline call went to /api/networking/groups/ (405) and
        // then fell back to a fake `g<timestamp>` id which 404'd on
        // every follow-up fetch; both of those modes are removed here so
        // a real error now surfaces as a toast instead of a broken chat.
        const memberIds = members
            .map((m) => Number(m.id))
            .filter((n) => Number.isFinite(n));
        try {
            const created = await createGroup(name, memberIds);
            const newGroup: ActiveGroupChat = {
                id: String(created.id),
                name: created.name || name,
                members: Array.isArray(created.members)
                    ? (created.members as unknown as Professional[])
                    : members,
            };
            setActiveGroupChat(newGroup);
            setActiveTab('messages');
            // Tell the inbox to refetch so the new group shows up in
            // the Rooms folder — the list was loaded before this POST
            // resolved, so without a nudge the user sees the detail
            // pane open on a group that isn't in the sidebar list.
            emitAppEvent('group-created', { groupId: newGroup.id });
        } catch (err) {
            console.error('[App] createGroup failed', err);
            addToast("Couldn't create group. Please try again.", 'error');
        }
    };

    // Render the active view based on selected tab
    const renderActiveView = ({ showMenu, onMenuClick }: LayoutChildProps) => {
        switch (activeTab) {
            case 'assistant':
                return (
                    <ChatRoom
                        messages={messages}
                        placeholder="Ask Chiefy anything about property development..."
                        title={selectedChatId ? "Chiefy Assistant" : "New Chat"}
                        isLoadingMessages={loadingMessages}
                        activeTab={activeTab}
                        onMenuClick={onMenuClick}
                        showMenu={showMenu}
                        chatId={selectedChatId || undefined}
                        projectId={selectedProjectId || undefined}
                        projects={sidebarProjects}
                        onMoveToProject={handleMoveToProject}
                        onCreateProject={handleCreateProjectFromChat}
                        onSendMessage={handleSendMessage}
                        userData={userData}
                        chatState={chatState}
                        onStop={stopStream}
                        onRegenerate={regenerateStream}
                    />
                );
            case 'networking':
                // Networking is people-discovery only now: Connections,
                // Requests, Discover, Groups list. DMs and group chats
                // live under the top-level Messages tab — handleMessageUser
                // and handleOpenGroupChat switch activeTab to 'messages',
                // which owns the ConversationDetail render.
                return (
                    <LazyShell scope="Networking" routeKey="networking">
                        <NetworkingView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            onMessageUser={handleMessageUser}
                            initialSubTab={networkingSubTab}
                            onSubTabChange={(sub) => handleTabChange('networking', sub)}
                        />
                    </LazyShell>
                );
            case 'education':
                return (
                    <LazyShell scope="Education" routeKey="education">
                        <EducationView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            initialSubTab="bootcamp"
                        />
                    </LazyShell>
                );
            case 'mentorship':
                // Dedicated top-level entry. EducationView branches to
                // MentorshipView when subTab='coaching'. Role-gated so
                // users without any mentorship role get the forbidden
                // screen — plan Phase F, kills the "what does this tab
                // do?" dead-end for non-mentorship users.
                return (
                    <RoleGate
                        route={ROUTES.mentorship}
                        user={userData}
                        onHome={() => handleTabChange('home')}
                        onMenuClick={onMenuClick}
                        showMenu={showMenu}
                    >
                        <LazyShell scope="Mentorship" routeKey="mentorship">
                            <EducationView
                                onMenuClick={onMenuClick}
                                showMenu={showMenu}
                                initialSubTab="coaching"
                            />
                        </LazyShell>
                    </RoleGate>
                );
            case 'profile':
                return (
                    <LazyShell scope="Profile" routeKey="profile">
                        <ProfileView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            userData={userData}
                            onNavigate={handleTabChange as unknown as (tab: string, subTab?: string) => void}
                        />
                    </LazyShell>
                );
            case 'settings':
                return (
                    <LazyShell scope="Settings" routeKey="settings">
                        <SettingsView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            userData={userData}
                            onNavigate={handleTabChange as unknown as (tab: string) => void}
                        />
                    </LazyShell>
                );
            case 'home':
                return (
                    <LazyShell scope="Home" routeKey="home">
                        <HomeView
                            user={userData}
                            onNavigate={handleTabChange}
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                        />
                    </LazyShell>
                );
            case 'messages': {
                // Top-level Messages tab. Always renders ConversationsView
                // (chrome header + rooms list + detail pane) so the user
                // keeps context. If App.tsx has a dmUser / activeGroupChat
                // set (clicked "Message" on someone), we seed the active
                // thread so the detail pane opens directly on that
                // conversation instead of the empty-state.
                let initialActive: ConversationSummary | null = null;
                if (dmUser) {
                    const isEmail = typeof dmUser.name === 'string' && dmUser.name.includes('@');
                    const displayName = isEmail
                        ? (dmUser.name.split('@')[0] || dmUser.name)
                        : (dmUser.name || 'Direct message');
                    initialActive = {
                        kind: 'direct',
                        id: String(dmUser.id),
                        display_name: displayName,
                        avatar_url: dmUser.avatar || '',
                        last_message: '',
                        last_message_at: null,
                        unread_count: 0,
                    };
                } else if (activeGroupChat) {
                    initialActive = {
                        kind: 'group',
                        id: String(activeGroupChat.id),
                        display_name: activeGroupChat.name,
                        avatar_url: '',
                        last_message: activeGroupChat.lastMessage || '',
                        last_message_at: activeGroupChat.lastMessageTime || null,
                        unread_count: activeGroupChat.unreadCount || 0,
                    };
                }
                return (
                    <LazyShell scope="Messages" routeKey="messaging">
                        <ConversationsView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            initialActive={initialActive}
                            onCreateGroup={handleCreateGroup}
                        />
                    </LazyShell>
                );
            }
            default:
                // Safety net: unrecognised tab falls back to the assistant view
                // so the user never sees a blank screen.
                return (
                    <ChatRoom
                        messages={messages}
                        placeholder="Ask Chiefy anything about property development..."
                        title="New Chat"
                        isLoadingMessages={loadingMessages}
                        activeTab={activeTab}
                        onMenuClick={onMenuClick}
                        showMenu={showMenu}
                        chatId={selectedChatId || undefined}
                        projectId={selectedProjectId || undefined}
                        projects={sidebarProjects}
                        onMoveToProject={handleMoveToProject}
                        onCreateProject={handleCreateProjectFromChat}
                        onSendMessage={handleSendMessage}
                        userData={userData}
                    />
                );
        }
    };

    return (
        <>
            {/* Session timeout warning — dismissible, resets on interaction */}
            {showSessionWarning && (
                <div className="session-warning" role="alert">
                    <span>Your session is about to expire.</span>
                    <button type="button" onClick={() => {
                        setShowSessionWarning(false);
                        // Touching the page resets the timeout hook automatically
                    }}>
                        Stay signed in
                    </button>
                </div>
            )}

            <Layout
                activeTab={activeTab}
                onTabChange={handleTabChange}
                educationSubTab={educationSubTab}
                networkingSubTab={networkingSubTab}
                userData={userData}
                onChatSelect={handleChatSelect}
                onNewDirectChat={handleNewDirectChat}
                selectedChatId={selectedChatId || undefined}
                selectedProjectId={selectedProjectId || undefined}
            >
                {(props) => renderActiveView(props)}
            </Layout>

            {/* Checkout success celebration */}
            <CheckoutSuccessModal
                open={showCheckoutSuccess}
                onClose={closeCheckoutSuccess}
                planName={userData?.active_plan?.plan?.name}
            />
        </>
    );
}

export default App;
